import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabaseClient';

interface Bloqueio {
  id: string;
  icone: string;
  label: string;
  sublabel: string;
  exercicioPath: string;
  exercicioNome: string;
  resultado: string;
  corIcone: string;
}

const bloqueios: Bloqueio[] = [
  {
    id: 'impostora',
    icone: 'ri-shield-keyhole-line',
    label: 'Síndrome da impostora',
    sublabel: 'Me sinto insuficiente mesmo com resultados',
    exercicioPath: '/exercicio-gratis/identidade-alta-performance',
    exercicioNome: 'Identidade de Alta Performance',
    resultado: 'Sua mente ainda não internalizou quem você se tornou. O exercício de Identidade de Alta Performance reconecta você às suas conquistas reais e reprograma o padrão de "nunca ser suficiente".',
    corIcone: 'text-gold-400',
  },
  {
    id: 'sabotagem',
    icone: 'ri-timer-flash-line',
    label: 'Sabotagem e procrastinação',
    sublabel: 'Sei o que fazer, mas não faço',
    exercicioPath: '/exercicio-gratis/tecnica-swish-pnl',
    exercicioNome: 'Técnica Swish — PNL',
    resultado: 'Existe um conflito interno entre o que você quer conscientemente e o que seu sistema nervoso foi programado para proteger. A Técnica Swish substitui esse padrão na raiz neurológica.',
    corIcone: 'text-blush-300',
  },
  {
    id: 'financeiro',
    icone: 'ri-money-dollar-circle-line',
    label: 'Bloqueio financeiro',
    sublabel: 'Dinheiro escapa das mãos ou nunca é suficiente',
    exercicioPath: '/exercicio-gratis/prosperidade-carreira',
    exercicioNome: 'Prosperidade e Carreira',
    resultado: 'Crenças sobre dinheiro são formadas antes dos 7 anos — e podem ser reprogramadas. O exercício de Prosperidade e Carreira remove bloqueios inconscientes que sabotam sua abundância.',
    corIcone: 'text-cream-400',
  },
  {
    id: 'relacionamentos',
    icone: 'ri-heart-3-line',
    label: 'Padrões em relacionamentos',
    sublabel: 'Os mesmos cenários se repetem sempre',
    exercicioPath: '/exercicio-gratis/amor-ideal',
    exercicioNome: 'Amor Ideal',
    resultado: 'Padrões relacionais são loops neurológicos gravados em experiências antigas. O exercício Amor Ideal usa PNL para reprogramar esses loops e atrair conexões genuínas e saudáveis.',
    corIcone: 'text-blush-300',
  },
  {
    id: 'ansiedade',
    icone: 'ri-mental-health-line',
    label: 'Ansiedade e excesso mental',
    sublabel: 'Mente acelerada, dificuldade de desacelerar',
    exercicioPath: '/exercicio-gratis/ancoragem-emocional',
    exercicioNome: 'Ancoragem Emocional',
    resultado: 'Seu sistema nervoso ficou preso no modo "ameaça". A Ancoragem Emocional instala estados de calma e foco que você pode ativar em segundos — como um botão de reset interno.',
    corIcone: 'text-gold-400',
  },
];

type Etapa = 'selecao' | 'captura' | 'resultado';

const FORM_URL = 'https://readdy.ai/api/form/d6u18mn6t0doov8ffma0';

export default function QuizBloqueio() {
  const navigate = useNavigate();
  const [etapa, setEtapa] = useState<Etapa>('selecao');
  const [bloqueioSelecionado, setBloqueioSelecionado] = useState<Bloqueio | null>(null);
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState('');

  const selecionarBloqueio = (b: Bloqueio) => {
    setBloqueioSelecionado(b);
    setEtapa('captura');
  };

  const voltar = () => {
    setEtapa('selecao');
    setBloqueioSelecionado(null);
    setErroEnvio('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim() || !email.trim() || !bloqueioSelecionado) return;

    setEnviando(true);
    setErroEnvio('');

    try {
      // 1. Envia para o formulário Readdy
      const body = new URLSearchParams({
        nome: nome.trim(),
        email: email.trim(),
        bloqueio: bloqueioSelecionado.label,
        exercicio_recomendado: bloqueioSelecionado.exercicioNome,
      });

      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      // 2. Salva lead no Supabase para analytics
      await supabase.from('link_bio_analytics').insert({
        tipo: 'lead',
        bloqueio: bloqueioSelecionado.label,
        exercicio_recomendado: bloqueioSelecionado.exercicioNome,
        nome: nome.trim(),
        email: email.trim(),
      });

      // 3. Redireciona para página de agradecimento com parâmetros
      const qs = new URLSearchParams({
        nome: nome.trim(),
        bloqueio: bloqueioSelecionado.label,
        exercicio: bloqueioSelecionado.exercicioNome,
        path: bloqueioSelecionado.exercicioPath,
      });
      navigate(`/obrigado-links?${qs.toString()}`);
    } catch {
      setErroEnvio('Erro ao enviar. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="mb-8">
      {/* Header do quiz */}
      <div className="text-center mb-5">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold-500/10 border border-gold-500/25 rounded-full mb-3">
          <i className="ri-sparkling-2-line text-gold-400 text-xs" />
          <span className="text-gold-400 text-[10px] font-bold tracking-[0.18em] uppercase">Quiz de 30 segundos</span>
        </div>
        <h2 className="font-display text-xl text-white font-semibold leading-snug mb-1">
          Qual é o seu maior bloqueio?
        </h2>
        <p className="text-white/40 text-xs leading-relaxed">
          Descubra o exercício certo para o seu padrão
        </p>
      </div>

      {/* ── ETAPA 1: SELEÇÃO ─────────────────────────────────────── */}
      {etapa === 'selecao' && (
        <div className="space-y-2.5">
          {bloqueios.map((b, i) => (
            <button
              key={b.id}
              onClick={() => selecionarBloqueio(b)}
              style={{ animationDelay: `${i * 60}ms` }}
              className="group w-full flex items-center gap-3.5 px-4 py-3.5 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.06] hover:border-gold-500/30 transition-all duration-200 cursor-pointer text-left animate-fade-in"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 border border-white/10 group-hover:bg-gold-500/10 group-hover:border-gold-500/20 transition-all shrink-0">
                <i className={`${b.icone} text-lg ${b.corIcone} opacity-70 group-hover:opacity-100 transition-opacity`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white/85 group-hover:text-white transition-colors leading-tight">
                  {b.label}
                </p>
                <p className="text-xs text-white/35 mt-0.5 leading-snug group-hover:text-white/50 transition-colors">
                  {b.sublabel}
                </p>
              </div>
              <div className="w-6 h-6 flex items-center justify-center shrink-0">
                <i className="ri-arrow-right-s-line text-base text-white/20 group-hover:text-gold-400/70 group-hover:translate-x-0.5 transition-all" />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* ── ETAPA 2: CAPTURA DE LEAD ─────────────────────────────── */}
      {etapa === 'captura' && bloqueioSelecionado && (
        <div className="bg-white/[0.03] border border-gold-500/20 rounded-2xl p-5">
          {/* Bloqueio escolhido */}
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-white/[0.06]">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gold-500/15 border border-gold-500/25 shrink-0">
              <i className={`${bloqueioSelecionado.icone} text-base text-gold-400`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-0.5">Seu bloqueio</p>
              <p className="text-sm font-semibold text-white/90 leading-tight">{bloqueioSelecionado.label}</p>
            </div>
            <button
              onClick={voltar}
              className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/5 text-white/30 hover:text-white/60 hover:bg-white/10 transition-all cursor-pointer"
            >
              <i className="ri-pencil-line text-xs" />
            </button>
          </div>

          <p className="text-white/60 text-sm leading-relaxed mb-5">
            Ótimo! Vou te enviar o exercício exato para esse padrão.
            <br />
            <span className="text-white/35 text-xs">Onde posso te mandar o material?</span>
          </p>

          <form
            data-readdy-form
            id="quiz-bloqueio-links"
            onSubmit={handleSubmit}
            className="space-y-3"
          >
            <input type="hidden" name="bloqueio" value={bloqueioSelecionado.label} />
            <input type="hidden" name="exercicio_recomendado" value={bloqueioSelecionado.exercicioNome} />

            <div>
              <input
                type="text"
                name="nome"
                placeholder="Seu primeiro nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                required
                className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold-500/50 focus:bg-white/[0.06] transition-all"
              />
            </div>
            <div>
              <input
                type="email"
                name="email"
                placeholder="Seu melhor e-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold-500/50 focus:bg-white/[0.06] transition-all"
              />
            </div>

            {erroEnvio && (
              <p className="text-xs text-red-400 text-center">{erroEnvio}</p>
            )}

            <button
              type="submit"
              disabled={enviando || !nome.trim() || !email.trim()}
              className="w-full py-3.5 rounded-xl bg-gradient-to-r from-gold-600 to-gold-500 text-luxury-black font-bold text-sm tracking-wide transition-all hover:from-gold-700 hover:to-gold-600 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
            >
              {enviando ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-base" />
                  Enviando...
                </>
              ) : (
                <>
                  <i className="ri-sparkling-2-line text-base" />
                  Receber meu exercício personalizado
                </>
              )}
            </button>

            <p className="text-center text-[10px] text-white/20 leading-relaxed">
              Sem spam. Você pode cancelar quando quiser.
            </p>
          </form>
        </div>
      )}
    </div>
  );
}
